import { useCallback } from 'react';
import { toast } from 'react-toastify';
import notificationManager from '../utils/notificationManager';

const useChamadaHandlers = ({
  senhaChamada,
  atendimentoFinalizado,
  chamadaEmAndamento,
  senhasOrdenadas,
  proximasSenhas,
  user,
  guicheSelecionado,
  senhaSelecionada,
  setSenhaChamada,
  setAtendimentoIniciado,
  setAtendimentoFinalizado,
  setTempoAtendimento,
  setProximasSenhas,
  setDialogoChamarSenhaEspecifica,
  setSenhaSelecionada,
  setAguardandoChamada,
  limparEstadoAtendimento,
  timerRef,
  chamarSenhaViaSocket,
  atualizarGuicheUsuario,
  updateUser
}) => {

  // Manipulador para chamar próxima senha
  const handleChamarProxima = useCallback(async () => {
    // Se já tem uma senha em atendimento e não está finalizada, não permitir chamar outra
    if (senhaChamada && !atendimentoFinalizado) {
      // Este caso será tratado pelo handleIniciarAtendimento
      return;
    }
    
    // Se tiver chamada em andamento, mostrar mensagem
    if (chamadaEmAndamento) {
      toast.info('Aguardando chamada da senha anterior para chamar a sua');
      setAguardandoChamada(true);
      return;
    }
    
    // Verificar se ainda há senhas na fila (usar senhas ordenadas)
    if (senhasOrdenadas.length > 0) {
      // Pega a primeira senha da fila ordenada
      const proximaSenha = senhasOrdenadas[0];
      
      try {
        // Chamar via WebSocket
        const sucesso = chamarSenhaViaSocket && chamarSenhaViaSocket(proximaSenha.id, false);
        
        if (sucesso) {
          // Remove a senha da fila proativamente (será recarregada pelo socket)
          const novasFila = proximasSenhas.filter(s => s.id !== proximaSenha.id);
          setProximasSenhas(novasFila);
          
          // Reseta os estados de atendimento
          setAtendimentoIniciado(false);
          setAtendimentoFinalizado(false);
          
          // Para o timer se estiver rodando
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          
          // Reseta o timer e a referência de tempo inicial
          setTempoAtendimento(0);
          
          // Limpar dados no localStorage
          limparEstadoAtendimento();
        }
        
      } catch (error) {
        console.error('Erro ao solicitar chamada da senha:', error);
        toast.error('Erro ao solicitar chamada da senha. Por favor, tente novamente.');
      }
    } else {
      // Não há mais senhas na fila
      toast.info('Não há mais senhas na fila de atendimento.');
    }
  }, [
    senhaChamada,
    atendimentoFinalizado,
    chamadaEmAndamento,
    senhasOrdenadas,
    proximasSenhas,
    setAguardandoChamada,
    chamarSenhaViaSocket,
    setProximasSenhas,
    setAtendimentoIniciado,
    setAtendimentoFinalizado,
    timerRef,
    setTempoAtendimento,
    limparEstadoAtendimento
  ]);

  // Manipulador para confirmar a chamada da senha específica
  const handleConfirmarChamarSenhaEspecifica = useCallback(async () => {
    if (senhaSelecionada && senhaSelecionada.id) {
      try {
        // Verificar se há chamadas em andamento
        if (chamadaEmAndamento) {
          toast.info('Aguardando chamada da senha anterior para chamar a sua');
          setAguardandoChamada(true);
          return;
        }
        
        // Chamar via WebSocket
        const sucesso = chamarSenhaViaSocket && chamarSenhaViaSocket(senhaSelecionada.id, false);
        
        if (sucesso) {
          // Remove a senha selecionada da fila proativamente
          const novasFila = proximasSenhas.filter(s => s.id !== senhaSelecionada.id);
          setProximasSenhas(novasFila);
          
          // Reseta os estados de atendimento
          setAtendimentoIniciado(false);
          setAtendimentoFinalizado(false);
          setTempoAtendimento(0);
        }
        
      } catch (error) {
        console.error('Erro ao comunicar com o servidor:', error);
        toast.error('Erro ao comunicar com o servidor. Por favor, tente novamente.');
      }
    } else {
      console.error('Senha selecionada inválida ou sem ID');
      toast.error('Erro ao chamar senha: dados inválidos');
    }
    
    setDialogoChamarSenhaEspecifica(false);
    setSenhaSelecionada(null);
  }, [
    senhaSelecionada,
    chamadaEmAndamento,
    proximasSenhas,
    chamarSenhaViaSocket,
    setAguardandoChamada,
    setProximasSenhas,
    setAtendimentoIniciado,
    setAtendimentoFinalizado,
    setTempoAtendimento,
    setDialogoChamarSenhaEspecifica,
    setSenhaSelecionada
  ]);

  // Função para atualizar o guichê no banco de dados
  const atualizarGuicheNoBanco = useCallback(async (novoGuiche) => {
    if (user && user.id) {
      try {
        // Salvar no banco de dados
        const resultado = await atualizarGuicheUsuario(user.id, novoGuiche);
        
        if (resultado.success) {
          console.log(`Guichê atualizado para: ${novoGuiche}`);
          
          // Atualizar o guichê no contexto de autenticação do usuário
          updateUser({
            guiche: novoGuiche
          });
          
          // Exibir notificação de sucesso (substitui a anterior se existir)
          notificationManager.guicheAlterado(novoGuiche);
        } else {
          console.error('Erro ao atualizar guichê:', resultado.error);
          toast.error('Erro ao atualizar guichê: ' + resultado.error);
        }
      } catch (error) {
        console.error('Erro ao atualizar guichê:', error);
        toast.error('Erro ao atualizar guichê. Por favor, tente novamente.');
      }
    }
  }, [user, atualizarGuicheUsuario, updateUser]);

  // Handler para alteração de guichê
  const handleGuicheChange = useCallback((event) => {
    const novoGuiche = event.target.value;
    atualizarGuicheNoBanco(novoGuiche);
  }, [atualizarGuicheNoBanco]);

  return {
    handleChamarProxima,
    handleConfirmarChamarSenhaEspecifica,
    handleGuicheChange,
    atualizarGuicheNoBanco
  };
};

export default useChamadaHandlers; 